/**
 * WebSocket Hook for Real-Time QR Billing Updates
 * Provides real-time connection to backend for owner dashboard
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { io } from 'socket.io-client';

const normalizeSocketUrl = (rawUrl) => {
  try {
    const url = new URL(rawUrl);
    const host = window?.location?.hostname;
    if (host && (url.hostname === 'localhost' || url.hostname === '127.0.0.1')) {
      url.hostname = host;
    }
    return url.toString().replace(/\/$/, '');
  } catch {
    return rawUrl;
  }
};

// ✅ CRITICAL FIX: Owner Dashboard must connect to CUSTOMER-MENU backend WebSocket
// because 'owner:counter_payment_request' event is emitted from customer menu backend (port 5001)
// Priority:
// 1. REACT_APP_CUSTOMER_MENU_SOCKET_URL (for explicit customer menu backend)
// 2. REACT_APP_SOCKET_URL (general socket URL)
// 3. REACT_APP_API_URL (fallback to API URL without /api)
// 4. Default production URLs based on environment
const SOCKET_URL = normalizeSocketUrl(
  process.env.REACT_APP_CUSTOMER_MENU_SOCKET_URL
    ? process.env.REACT_APP_CUSTOMER_MENU_SOCKET_URL.replace(/\/$/, '')
    : process.env.REACT_APP_SOCKET_URL 
      ? process.env.REACT_APP_SOCKET_URL.replace(/\/$/, '') 
      : process.env.REACT_APP_API_URL 
        ? process.env.REACT_APP_API_URL.replace('/api', '').replace(/\/$/, '') 
        : (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
          ? 'http://localhost:5001'  // Local: Connect to customer menu backend
          : 'https://menu.invexis.co.in'  // Production: Connect to customer menu backend
);


/**
 * Custom hook for WebSocket connection
 * @param {number} businessId - Business ID for this connection
 * @param {number} userId - User ID (owner)
 * @param {Function} onDashboardUpdate - Callback when dashboard data updates
 * @param {Function} onNewOrder - Callback when new order is placed
 * @param {Function} onPaymentCompleted - Callback when payment is completed
 * @param {Function} onPaymentConfirmed - Callback when payment is confirmed by waiter
 * @param {Function} onOrderStatusChanged - Callback when order status changes
 * @param {Function} onWaiterCall - Callback when customer calls waiter
 * @param {Function} onPaymentAtCounter - Callback when payment is made at counter
 * @param {Function} onCounterPaymentRequest - Callback when customer requests to pay at counter (needs owner confirmation)
 * @param {Function} onTakeawayOrderUpdate - Callback when takeaway order is placed/updated
 */
export const useWebSocket = ({
  businessId,
  userId,
  onDashboardUpdate,
  onNewOrder,
  onPaymentCompleted,
  onPaymentConfirmed,
  onOrderStatusChanged,
  onNotification,
  onWaiterCall,
  onPaymentAtCounter,
  onCounterPaymentRequest,
  onTakeawayOrderUpdate,
  onOrderConfirmed
}) => {
  const socketRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const connectionInitialized = useRef(false);
  const MAX_RECONNECT_ATTEMPTS = 5;

  // Store callbacks in refs to avoid dependency issues
  const callbacksRef = useRef({
    onDashboardUpdate,
    onNewOrder,
    onPaymentCompleted,
    onPaymentConfirmed,
    onOrderStatusChanged,
    onNotification,
    onWaiterCall,
    onPaymentAtCounter,
    onCounterPaymentRequest,
    onTakeawayOrderUpdate,
    onOrderConfirmed
  });

  // Update callbacks ref when they change
  useEffect(() => {
    callbacksRef.current = {
      onDashboardUpdate,
      onNewOrder,
      onPaymentCompleted,
      onPaymentConfirmed,
      onOrderStatusChanged,
      onNotification,
      onWaiterCall,
      onPaymentAtCounter,
      onCounterPaymentRequest,
      onTakeawayOrderUpdate,
      onOrderConfirmed
    };
  }, [onDashboardUpdate, onNewOrder, onPaymentCompleted, onPaymentConfirmed, onOrderStatusChanged, onNotification, onWaiterCall, onPaymentAtCounter, onCounterPaymentRequest, onTakeawayOrderUpdate, onOrderConfirmed]);

  // Initialize WebSocket connection
  const connect = useCallback(() => {
    if (!businessId) {
      console.warn('⚠️  Cannot connect WebSocket: No business ID');
      return;
    }

    // Prevent multiple connection attempts
    if (connectionInitialized.current && socketRef.current?.connected) {
      return;
    }

    // Close existing connection before creating new one
    if (socketRef.current) {
      socketRef.current.removeAllListeners();
      socketRef.current.close();
      socketRef.current = null;
    }

    try {
      
      const socket = io(SOCKET_URL, {
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionDelay: 3000,        // ✅ 3 seconds (more conservative)
        reconnectionDelayMax: 15000,    // ✅ 15 seconds max
        reconnectionAttempts: 3,        // ✅ Reduced to 3 attempts
        timeout: 20000,                 // ✅ 20 seconds timeout
        autoConnect: true,
        forceNew: false,                // ✅ Reuse existing connection if possible
        multiplex: true                 // ✅ Multiplex over single connection
      });

      socketRef.current = socket;
      connectionInitialized.current = true;

      // Connection established
      socket.on('connect', () => {
        setIsConnected(true);
        setConnectionError(null);
        reconnectAttempts.current = 0;

        // Register as owner for this business
        if (businessId) {
          socket.emit('owner:register', {
            businessId,
            userId: userId || null
          });
        } else {
          console.warn('⚠️  Cannot register as owner: Missing businessId', { businessId, userId });
        }
      });

      // Initial dashboard data
      socket.on('dashboard:initial', (data) => {
        if (callbacksRef.current.onDashboardUpdate) {
          callbacksRef.current.onDashboardUpdate(data);
        }
      });

      // Dashboard updates
      socket.on('dashboard:update', (data) => {
        
        if (callbacksRef.current.onDashboardUpdate) {
          callbacksRef.current.onDashboardUpdate(data);
        }
      });

      // New order notification
      socket.on('order:new', (data) => {
        
        // Play sound notification
        playNotificationSound('new-order');
        
        // Show browser notification if permitted
        showBrowserNotification('New Order', `Table ${data.tableNumber} - ₹${data.total}`);
        
        if (callbacksRef.current.onNewOrder) {
          callbacksRef.current.onNewOrder(data);
        }
      });

      // Payment completed notification
      socket.on('payment:completed', (data) => {
        
        // Play sound notification
        playNotificationSound('payment-success');
        
        // Show browser notification if permitted
        showBrowserNotification('Payment Received', `Table ${data.tableNumber} - ₹${data.total}`);
        
        if (callbacksRef.current.onPaymentCompleted) {
          callbacksRef.current.onPaymentCompleted(data);
        }
      });

      // Payment confirmed by waiter (for green → ash transition timing)
      socket.on('payment:confirmed', (data) => {
        
        if (callbacksRef.current.onPaymentConfirmed) {
          callbacksRef.current.onPaymentConfirmed(data);
        }
      });

      // Order status changed
      socket.on('order:status_changed', (data) => {
        if (callbacksRef.current.onOrderStatusChanged) {
          callbacksRef.current.onOrderStatusChanged(data);
        }
      });

      // Waiter call - Table needs assistance
      socket.on('waiter:call', (data) => {
        
        // Play urgent notification sound
        playNotificationSound('alert');
        
        // Show browser notification
        showBrowserNotification('🔔 Waiter Called', `Table ${data.tableNumber} needs assistance`);
        
        if (callbacksRef.current.onWaiterCall) {
          callbacksRef.current.onWaiterCall(data);
        }
      });

      // Payment at counter - Requires owner confirmation
      socket.on('payment_at_counter', (data) => {
        // Play notification sound
        playNotificationSound('alert');
        
        // Show browser notification
        showBrowserNotification('💰 Payment Received', `Table ${data.tableNumber} - ₹${data.amount} paid at counter`);
        
        if (callbacksRef.current.onPaymentAtCounter) {
          callbacksRef.current.onPaymentAtCounter(data);
        }
      });

      // Owner counter payment request - Customer clicked "Pay at Counter" button
      socket.on('owner:counter_payment_request', (data) => {
        // Play urgent notification sound
        playNotificationSound('alert');
        
        // Show browser notification
        showBrowserNotification('💰 Counter Payment Request', `Table ${data.tableNumber} - ₹${data.amount} - Please confirm`);
        
        if (callbacksRef.current.onCounterPaymentRequest) {
          callbacksRef.current.onCounterPaymentRequest(data);
        } else {
          console.warn('⚠️ onCounterPaymentRequest callback not defined!');
        }
      });

      // Takeaway order update
      socket.on('takeaway_order_update', (data) => {
        
        // Play notification sound
        playNotificationSound('new-order');
        
        // Show browser notification
        showBrowserNotification(' New Takeaway Order', `Order #${data.orderId} received`);
        
        if (callbacksRef.current.onTakeawayOrderUpdate) {
          callbacksRef.current.onTakeawayOrderUpdate(data);
        }
      });

      // Owner notification for orders when dashboards are disabled
      socket.on('owner:new_order_notification', (data) => {
        
        // Play notification sound
        playNotificationSound('alert');
        
        // Show browser notification
        showBrowserNotification('📬 New Order Needs Approval', `Table ${data.tableNumber} - ₹${data.total}`);
        
        if (callbacksRef.current.onOwnerOrderNotification) {
          callbacksRef.current.onOwnerOrderNotification(data);
        }
      });

      // Order confirmed by owner - refresh dashboard to show table turning green
      socket.on('order_confirmed', (data) => {
        
        // Trigger dashboard refresh to show updated table state
        if (callbacksRef.current.onOrderConfirmed) {
          callbacksRef.current.onOrderConfirmed(data);
        }
      });

      // Generic notification
      socket.on('notification', (data) => {
        
        if (data.priority === 'high' || data.priority === 'critical') {
          playNotificationSound('alert');
        }
        
        if (callbacksRef.current.onNotification) {
          callbacksRef.current.onNotification(data);
        }
      });

      // Connection error
      socket.on('connect_error', (error) => {
        console.error('❌ WebSocket connection error:', error.message);
        setConnectionError(error.message);
        setIsConnected(false);
        
        reconnectAttempts.current += 1;
        
        if (reconnectAttempts.current >= MAX_RECONNECT_ATTEMPTS) {
          console.error('❌ Max reconnection attempts reached');
          socket.disconnect();
        }
      });

      // Disconnection
      socket.on('disconnect', (reason) => {
        setIsConnected(false);

        // Attempt to reconnect if disconnection wasn't intentional
        if (reason === 'io server disconnect') {
          // Server initiated disconnect, reconnect manually
          socket.connect();
        }
      });

      // Error handling
      socket.on('error', (error) => {
        console.error('❌ WebSocket error:', error);
        setConnectionError(error.message || 'WebSocket error');
      });

    } catch (error) {
      console.error('❌ Failed to create WebSocket connection:', error);
      setConnectionError(error.message);
    }
  }, [businessId, userId]); // Include businessId and userId

  // Disconnect WebSocket
  const disconnect = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.removeAllListeners();
      socketRef.current.disconnect();
      socketRef.current = null;
      setIsConnected(false);
      connectionInitialized.current = false;
    }
    
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
  }, []);

  // Send custom event
  const emit = useCallback((event, data) => {
    if (socketRef.current && isConnected) {
      socketRef.current.emit(event, data);
    } else {
      console.warn('⚠️  Cannot emit event: WebSocket not connected');
    }
  }, [isConnected]);

  // Connect on mount ONLY when businessId is available
  useEffect(() => {
    if (businessId && !connectionInitialized.current) {
      connect();
    }
    
    return () => {
      disconnect();
    };
  }, [businessId]); // Only reconnect if businessId changes

  return {
    isConnected,
    connectionError,
    socket: socketRef.current,
    emit,
    reconnect: connect,
    disconnect
  };
};

/**
 * Play notification sound
 */
const playNotificationSound = (type) => {
  try {
    // Create audio context if needed
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    // Different sounds for different notification types
    const frequencies = {
      'new-order': 880, // A5
      'payment-success': 1047, // C6
      'alert': 659 // E5
    };
    
    const frequency = frequencies[type] || 440;
    
    // Create oscillator
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    // Set sound properties
    oscillator.frequency.value = frequency;
    oscillator.type = 'sine';
    
    // Fade in and out
    gainNode.gain.setValueAtTime(0, audioContext.currentTime);
    gainNode.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.1);
    gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + 0.5);
    
    // Play sound
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.5);
    
    // For payment success, play two tones
    if (type === 'payment-success') {
      const oscillator2 = audioContext.createOscillator();
      const gainNode2 = audioContext.createGain();
      
      oscillator2.connect(gainNode2);
      gainNode2.connect(audioContext.destination);
      
      oscillator2.frequency.value = 1319; // E6
      oscillator2.type = 'sine';
      
      gainNode2.gain.setValueAtTime(0, audioContext.currentTime + 0.2);
      gainNode2.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.3);
      gainNode2.gain.linearRampToValueAtTime(0, audioContext.currentTime + 0.7);
      
      oscillator2.start(audioContext.currentTime + 0.2);
      oscillator2.stop(audioContext.currentTime + 0.7);
    }
    
  } catch (error) {
    console.warn('⚠️  Could not play notification sound:', error);
  }
};

/**
 * Show browser notification
 */
const showBrowserNotification = (title, body) => {
  try {
    // Request permission if not granted
    if ('Notification' in window) {
      if (Notification.permission === 'granted') {
        new Notification(title, {
          body,
          icon: '/logo192.png',
          badge: '/logo192.png',
          tag: 'qr-billing-notification',
          requireInteraction: false,
          silent: false
        });
      } else if (Notification.permission !== 'denied') {
        Notification.requestPermission().then(permission => {
          if (permission === 'granted') {
            new Notification(title, {
              body,
              icon: '/logo192.png'
            });
          }
        });
      }
    }
  } catch (error) {
    console.warn('⚠️  Could not show browser notification:', error);
  }
};

export default useWebSocket;
